import React, { useState } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Radio,
  Slider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSuccess } from "../../../../Redux/AlertReducer";
import { Helper } from "../../../../tools/Helper";
import { api_Routes } from "../../../../tools/api_Routes";
import { errorToaster } from "../../../../helper/toasterConfiguration";
import Files from "react-files";

export const FormEditBanner = ({ data, setData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fonts = ["Mockads", "Arial", "Great Vibes"];
  const colors = ["#FFFFFF", "#303030", "#ECA601"];

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [uploadOption, setUploadOption] = useState(data?.logo ? "logo" : "customLogo");
  const [selectedFont, setSelectedFont] = useState(
      data?.customLogo && data?.customLogo?.fontFamily
  );
  const [selectedColor, setSelectedColor] = useState(
      data?.customLogo && data?.customLogo?.color
  );
  const [selectedBgColor, setSelectedBgColor] = useState(
      data?.customLogo && data?.customLogo?.logo_bg_color
  );
  const [logoName, setLogoName] = useState(
      data?.customLogo && data?.customLogo?.logoName
  );
  const [fontSize, setFontSize] = useState(
      data?.customLogo && data?.customLogo?.fontSize?.replace("px", "") || 44
  );
  const [logoFile, setLogoFile] = useState(null);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      // Extract the actual File object from the react-files response
      const file = files[0];
      setLogoFile(file);
    }
  };

  const handleFontChange = (font) => {
    setSelectedFont(font);
  };

  const handleColorClick = (color) => {
    setSelectedColor(color);
  };

  const handleFontSizeChange = (event, newValue) => {
    setFontSize(newValue);
  };

  const toggleUploadOption = () => {
    if (uploadOption === "logo") {
      setUploadOption("customLogo");
    } else {
      setUploadOption("logo");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missingFields = [];

    if (uploadOption === "customLogo") {
      if (!logoName || !selectedColor || !selectedFont || !fontSize) {
        missingFields.push(
            "customLogo properties (logoName, color, fontFamily, fontSize)"
        );
      }
    } else if (uploadOption === "logo" && !logoFile && !data.logo) {
      missingFields.push("logo");
    }

    if (missingFields.length > 0) {
      errorToaster(
          `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      setIsLoadingSubmit(false);
      return;
    }

    setIsLoadingSubmit(true);

    const formDataToSend = new FormData();

    // Add title and description
    formDataToSend.append("title", data.title);
    formDataToSend.append("description", data.description);

    if (uploadOption === "customLogo") {
      // Handle custom logo
      const customLogoData = {
        fontSize: `${fontSize}px`,
        color: selectedColor,
        fontFamily: selectedFont,
        logoName: logoName,
      };
      formDataToSend.append("customLogo", JSON.stringify(customLogoData));
    } else {
      // Handle logo file upload
      if (logoFile) {
        formDataToSend.append("logo", logoFile);
      } else if (data.logo && typeof data.logo === "string") {
        // If logo is a string (existing logo), send it as is
        formDataToSend.append("logo", data.logo);
      }
    }

    try {
      const { result, response, message } = await Helper.Post({
        url: api_Routes.BannersAdvertising.Edit + id,
        data: formDataToSend,
        isFormData: true,
      });

      setIsLoadingSubmit(false);
      if (response) {
        dispatch(setSuccess(true));
        navigate("/Ads-Main-Page");
      }
    } catch (error) {
      console.error("An error occurred during form submission:", error);
      setIsLoadingSubmit(false);
    }
  };

  return (
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 items-center gap-4">
          <TextField
              fullWidth
              margin="normal"
              label="Title"
              name="title"
              required
              autoComplete="off"
              value={data?.title}
              onChange={handleInputChange}
              variant="outlined"
          />
        </div>
        <div className="grid grid-cols-1 items-center gap-4">
          <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="description"
              required
              type="description"
              autoComplete="off"
              value={data.description}
              onChange={handleInputChange}
              variant="outlined"
          />
        </div>

        {/* Logo Section */}
        <div style={{ marginTop: "20px" }}>
          <Typography
              sx={{
                marginBottom: "10px",
                fontWeight: "600",
                fontSize: "24px",
                lineHeight: "30px",
                color: "#474747",
              }}
          >
            Banner Logo *
          </Typography>

          <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "20px 0",
                width: "100%",
              }}
          >
            <Button
                variant="outlined"
                onClick={toggleUploadOption}
                sx={{
                  fontWeight: "600",
                  border: "1px solid #3F78E0",
                  textTransform: "capitalize",
                  textAlign: "center",
                  borderRadius: "10px",
                  padding: "3px 40px",
                  background: "#FFFFFF",
                  color: "#3F78E0",
                  boxShadow: "0px 5px 5px #00000040, inset 4px 7px 5px #FFFFFF30",
                  "&:hover": {
                    background: "none",
                  },
                }}
            >
              {uploadOption === "logo"
                  ? "Switch to Custom Logo"
                  : "Switch to Photo Upload"}
            </Button>
          </Box>

          <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                position: "relative",
                justifyContent: "space-between",
                width: "100%",
                gap: "20px",
              }}
          >
            {/* Logo Upload Section */}
            <Box
                sx={{
                  width: { xs: "100%", md: "45%" },
                  opacity: uploadOption === "customLogo" ? 0.5 : 1,
                  pointerEvents: uploadOption === "customLogo" ? "none" : "auto",
                }}
            >
              <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: { xs: "300px", md: "100%", lg: "300px" },
                    textAlign: "center",
                    backgroundColor: "#FFFFFF",
                    cursor: uploadOption === "customLogo" ? "not-allowed" : "pointer",
                    position: "relative",
                    border: "#303030 solid 1px",
                    borderRadius: "15px",
                  }}
              >
                <Files
                    onChange={handleFileChange}
                    onError={(error) => console.error("File upload error:", error)}
                    accepts={["image/*"]}
                    clickable
                    multiple={false}
                    maxFileSize={10485760}
                    disabled={uploadOption === "customLogo"}
                    style={{
                      opacity: 0,
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      top: 0,
                      left: 0,
                    }}
                />
                {logoFile || data.logo ? (
                    <Box
                        sx={{
                          position: "relative",
                          width: "80%",
                          height: "80%",
                          borderRadius: "10px",
                          overflow: "hidden",
                        }}
                    >
                      <img
                          src={
                            logoFile instanceof File || logoFile instanceof Blob
                                ? URL.createObjectURL(logoFile)
                                : data.logo
                          }
                          alt="Banner Logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: "10px",
                          }}
                      />
                      <IconButton
                          onClick={() => {
                            setLogoFile(null);
                            setData((prev) => ({ ...prev, logo: null }));
                          }}
                          sx={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                            color: "white",
                            backgroundColor: "red",
                            borderRadius: "10px",
                            "&:hover": {
                              backgroundColor: "red",
                            },
                          }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                      Click to upload a banner logo
                    </Typography>
                )}
              </Box>
            </Box>

            {/* Custom Logo Section */}
            <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  opacity: uploadOption === "logo" ? 0.5 : 1,
                  pointerEvents: uploadOption === "logo" ? "none" : "auto",
                  background: "#FFFFFF",
                  paddingBottom: "25px",
                  border: "1px solid #303030",
                  borderRadius: "10px",
                }}
            >
              <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      sm: "row",
                      md: "column",
                      lg: "row",
                    },
                    justifyContent: {
                      xs: "space-between",
                      sm: "space-around",
                      md: "space-between",
                    },
                    alignItems: "center",
                    height: "100%",
                    padding: {
                      xs: "20px 20px",
                      sm: "0 20px",
                      md: "20px 20px",
                      lg: "0 20px",
                    },
                  }}
              >
                <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      width: "90%",
                    }}
                >
                  <Box>
                    <Typography
                        sx={{
                          color: "#474747",
                          fontSize: "20px",
                          fontWeight: "600",
                          lineHeight: "30px",
                        }}
                    >
                      Pick a Custom Font
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={logoName}
                        onChange={(e) => setLogoName(e.target.value)}
                        placeholder="Type Your Logo Name here"
                        size="small"
                        InputLabelProps={{
                          style: {
                            fontSize: "16px",
                            color: "#5F5F5F",
                            lineHeight: "24px",
                            fontWeight: "600",
                            letterSpacing: "1px",
                          },
                        }}
                        sx={{
                          width: "95%",
                          boxShadow: "-2px 2px 4px #00000040",
                          borderRadius: "10px",
                          backgroundColor: "white",
                          marginTop: "10px",
                        }}
                    />
                  </Box>

                  <Box>
                    <Typography>Choose a Font</Typography>
                    <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "10px",
                          marginTop: "4px",
                          justifyContent: "space-evenly",
                        }}
                    >
                      {fonts.map((font) => (
                          <Box
                              key={font}
                              sx={{
                                background: "#E5EDFF",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                gap: "4px",
                                border: "1px solid #959595",
                                borderRadius: "10px",
                                padding: {
                                  xs: "4px 6px",
                                  sm: "4px 12px",
                                  md: "4px 25px",
                                },
                              }}
                          >
                            <Typography
                                sx={{
                                  fontFamily: font,
                                  fontSize: "20px",
                                  lineHeight: "24px",
                                  fontWeight: "600",
                                  color: "#474747",
                                }}
                            >
                              lorem
                            </Typography>
                            <Radio
                                checked={selectedFont === font}
                                onChange={() => handleFontChange(font)}
                                sx={{
                                  color: "primary.main",
                                }}
                            />
                          </Box>
                      ))}
                    </Box>
                  </Box>
                  <Typography>Choose a color and font size</Typography>
                  <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "10px",
                      }}
                  >
                    <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                    >
                      <Box sx={{ display: "flex", gap: 2 }}>
                        {colors.map((color) => (
                            <Box
                                key={color}
                                onClick={() => handleColorClick(color)}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  backgroundColor: color,
                                  cursor: "pointer",
                                  border: "1px #303030 solid",
                                  transition: "border 0.3s ease",
                                }}
                            />
                        ))}
                      </Box>
                    </Box>
                    <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                    >
                      <Slider
                          value={fontSize}
                          onChange={handleFontSizeChange}
                          min={20}
                          max={50}
                          step={1}
                          valueLabelDisplay="auto"
                          sx={{ width: "100px" }}
                      />
                      <Typography variant="body2">{fontSize}px</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "6px",
                      height: "200px",
                    }}
                >
                  <Typography
                      sx={{
                        fontSize: "18px",
                        lineHeight: "28px",
                        color: "#474747",
                      }}
                  >
                    Your Logo demo
                  </Typography>
                  <Box
                      sx={{
                        background: "radial-gradient(circle, #51AAFD, #002D68)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        padding: "40px",
                        borderRadius: "15px",
                        width: "100px",
                        height: "80px",
                        overflow: "hidden",
                      }}
                  >
                    <Typography
                        sx={{
                          fontSize: `${fontSize}px`,
                          fontWeight: "700",
                          color: selectedColor,
                          fontFamily: selectedFont,
                        }}
                    >
                      {logoName || "LOREM"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </div>

        <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoadingSubmit}
            color="primary"
            sx={{
              display: isLoadingSubmit ? "flex" : "block",
              marginTop: 2,
              width: "20%",
              marginX: "auto",
              fontWeight: "bold",
              justifyContent: "center",
            }}
        >
          {isLoadingSubmit ? (
              <CircularProgress size={24} color="primary" />
          ) : (
              "Submit"
          )}
        </Button>
      </form>
  );
};